import type { AiConfidence, AiVerdict } from '../domain/aiObservation';
import type { AreaGroup, AreaKind } from '../domain/areaCatalog';
import type { FindingClass, FindingPriority, FindingStatus } from '../domain/finding';
import {
  DEFAULT_PLAN_NAMING,
  ROOM_PRESETS,
  SPACE_PRESETS,
  SYSTEM_PRESETS,
  areaSectionKindOf,
  planCounts,
  type AreaSection,
  type FloorPlan,
  type HousePlan,
  type PlanNaming,
  type RoomKey,
  type SpaceKey,
  type SystemKey
} from '../domain/housePlan';
import { isInspectionPurged, type BasementFinish, type BasementType, type InspectionStatus, type ItemStatus, type PropertyType, type UnableReason, type V2Area, type V2ChecklistItem, type V2Inspection } from '../domain/inspection';
import type { InspectionService } from '../domain/inspectionService';
import type { ReportDue } from '../domain/reportDue';
import type { GateNoteCode } from '../domain/scopeGate';
import type { ProhibitedPhraseId } from '../domain/authority/ny/prohibitedPhrases';
import type { WordingSource } from '../domain/wordingGuard';
import type { ResponsibleTrade } from '../domain/responsibleTrade';
import type { DefectSeverity } from '../domain/severity';
import type { DescriptorAnswer, DescriptorDefinition, DescriptorKey, HeatingFuel, UtilitySource } from '../domain/descriptors';
import type { CheckTypeId } from '../report/rowWording';
import { translate, type MessageKey, type Translator } from './i18n';
import type { AppLocale } from './locales';
import { isTemplateHint, templateRowById } from '../domain/checklistTemplates';
import { CHECKLIST_TEMPLATE_EN_US } from '../domain/checklistTemplates.en-US';

// Display labels for domain enumerations (Final Implementation Authority §21.3 / §21.4): the domain
// keeps language-neutral codes (and its Chinese naming constants for area instance names); every
// user-visible label comes from the two dictionaries through these typed maps. Nothing here is a
// second vocabulary — it is the ONE glossary, keyed.
type T = Translator['t'];

const AREA_KIND_KEYS: Record<AreaKind, MessageKey> = Object.freeze({
  exterior: 'areaKind.exterior',
  roof: 'areaKind.roof',
  garage: 'areaKind.garage',
  basement: 'areaKind.basement',
  structure: 'areaKind.structure',
  electrical: 'areaKind.electrical',
  plumbing: 'areaKind.plumbing',
  waterHeater: 'areaKind.waterHeater',
  hvac: 'areaKind.hvac',
  attic: 'areaKind.attic',
  interior: 'areaKind.interior',
  kitchen: 'areaKind.kitchen',
  bathroom: 'areaKind.bathroom',
  bedroom: 'areaKind.bedroom',
  safety: 'areaKind.safety',
  other: 'areaKind.other'
});

const AREA_GROUP_KEYS: Record<AreaGroup, MessageKey> = Object.freeze({
  exterior: 'areaGroup.exterior',
  spaces: 'areaGroup.spaces',
  systems: 'areaGroup.systems',
  rooms: 'areaGroup.rooms',
  wholeHouse: 'areaGroup.wholeHouse'
});

const ROOM_KEYS: Record<RoomKey, MessageKey> = Object.freeze({
  kitchen: 'room.kitchen',
  living: 'room.living',
  family: 'room.family',
  dining: 'room.dining',
  bedroom: 'room.bedroom',
  fullBath: 'room.fullBath',
  halfBath: 'room.halfBath',
  laundry: 'room.laundry'
});

// Instance-name stems differ from the preset labels only for the full bath ("Full Bath" label,
// "Bathroom" name) — the same distinction the Chinese presets make (全卫 → 卫生间).
const ROOM_BASE_KEYS: Record<RoomKey, MessageKey> = Object.freeze({ ...ROOM_KEYS, fullBath: 'room.fullBathBase' });

const SPACE_KEYS: Record<SpaceKey, MessageKey> = Object.freeze({
  garage: 'space.garage',
  detachedGarage: 'space.detachedGarage',
  carport: 'space.carport',
  basement: 'space.basement',
  crawlspace: 'space.crawlspace',
  attic: 'space.attic'
});

const PROPERTY_TYPE_KEYS: Record<PropertyType, MessageKey> = Object.freeze({
  singleFamily: 'propertyType.singleFamily',
  multiFamily: 'propertyType.multiFamily',
  condo: 'propertyType.condo',
  townhouse: 'propertyType.townhouse',
  other: 'propertyType.other'
});

const INSPECTION_STATUS_KEYS: Record<InspectionStatus, MessageKey> = Object.freeze({
  draft: 'inspectionStatus.draft',
  inProgress: 'inspectionStatus.inProgress',
  completed: 'inspectionStatus.completed'
});

const RESULT_KEYS: Record<ItemStatus, MessageKey> = Object.freeze({
  notChecked: 'result.pending',
  checkedOk: 'result.pass',
  issueFound: 'result.defect',
  unableToInspect: 'result.unableToInspect',
  notApplicable: 'result.na'
});

const FINDING_CLASS_KEYS: Record<FindingClass, MessageKey> = Object.freeze({
  defect: 'findingClass.defect',
  safety: 'findingClass.safety',
  maintenance: 'findingClass.maintenance',
  advisory: 'findingClass.advisory',
  informational: 'findingClass.informational'
});

const FINDING_STATUS_KEYS: Record<FindingStatus, MessageKey> = Object.freeze({
  draft: 'findingStatus.draft',
  confirmed: 'findingStatus.confirmed',
  rejected: 'findingStatus.rejected'
});

const UNABLE_REASON_KEYS: Record<UnableReason, MessageKey> = Object.freeze({
  obstructed: 'reason.obstructed',
  inaccessible: 'reason.inaccessible',
  utilitiesOff: 'reason.utilitiesOff',
  unsafeCondition: 'reason.unsafeCondition',
  weather: 'reason.weather',
  notInstalled: 'reason.notInstalled',
  other: 'reason.other'
});

const BASEMENT_TYPE_KEYS: Record<BasementType, MessageKey> = Object.freeze({
  full: 'basementType.full',
  partial: 'basementType.partial',
  crawlspace: 'basementType.crawlspace',
  slab: 'basementType.slab',
  none: 'basementType.none'
});

const BASEMENT_FINISH_KEYS: Record<BasementFinish, MessageKey> = Object.freeze({
  unfinished: 'basementFinish.unfinished',
  finished: 'basementFinish.finished',
  partial: 'basementFinish.partial',
  mixed: 'basementFinish.mixed',
  unknown: 'basementFinish.unknown'
});

const TRADE_KEYS: Record<ResponsibleTrade, MessageKey> = Object.freeze({
  licensedElectrician: 'trade.licensedElectrician',
  licensedPlumbingProfessional: 'trade.licensedPlumbingProfessional',
  qualifiedHvacContractor: 'trade.qualifiedHvacContractor',
  structuralEngineer: 'trade.structuralEngineer',
  qualifiedRoofingContractor: 'trade.qualifiedRoofingContractor',
  licensedPestProfessional: 'trade.licensedPestProfessional',
  qualifiedGeneralContractor: 'trade.qualifiedGeneralContractor',
  qualifiedMasonryContractor: 'trade.qualifiedMasonryContractor',
  qualifiedSidingContractor: 'trade.qualifiedSidingContractor',
  qualifiedWindowDoorContractor: 'trade.qualifiedWindowDoorContractor',
  qualifiedGarageDoorTechnician: 'trade.qualifiedGarageDoorTechnician',
  qualifiedServiceTechnician: 'trade.qualifiedServiceTechnician',
  qualifiedApplianceTechnician: 'trade.qualifiedServiceTechnician',
  qualifiedChimneyProfessional: 'trade.qualifiedChimneyProfessional',
  qualifiedInsulationContractor: 'trade.qualifiedInsulationContractor',
  qualifiedWaterproofingContractor: 'trade.qualifiedWaterproofingContractor',
  qualifiedDrainageGradingContractor: 'trade.qualifiedDrainageGradingContractor',
  qualifiedPavingContractor: 'trade.qualifiedPavingContractor',
  licensedMoldAssessor: 'trade.licensedMoldAssessor',
  other: 'trade.other'
});

const FLOOR_KEYS: readonly MessageKey[] = Object.freeze(['floor.1', 'floor.2', 'floor.3', 'floor.4', 'floor.5', 'floor.6', 'floor.7', 'floor.8']);

export function areaKindLabel(t: T, kind: AreaKind): string {
  return t(AREA_KIND_KEYS[kind]);
}

export function areaGroupLabel(t: T, group: AreaGroup): string {
  return t(AREA_GROUP_KEYS[group]);
}

// "建造年份（可选）" / "Year built (Optional)": a label with its tag in the locale's own brackets
// (Owner-selected form design 2026-09-14, where the tag is part of the row label).
export function labelWithTag(locale: AppLocale, label: string, tag: string): string {
  return locale === 'zh-CN' ? `${label}（${tag}）` : `${label} (${tag})`;
}

export function floorLabelFor(t: T, floor: number): string {
  const key = FLOOR_KEYS[floor - 1];
  return key ? t(key) : t('floor.n', { n: floor });
}

// Group headers of the Property Layout / Areas list, derived from the section the domain computes.
export function areaSectionLabel(t: T, section: AreaSection): string {
  const kind = areaSectionKindOf(section);
  return kind.kind === 'floor' ? floorLabelFor(t, kind.floor ?? 1) : areaGroupLabel(t, kind.kind);
}

export function roomLabel(t: T, key: RoomKey): string {
  return t(ROOM_KEYS[key]);
}

export function spaceLabel(t: T, key: SpaceKey): string {
  return t(SPACE_KEYS[key]);
}

export function systemLabel(t: T, key: SystemKey): string {
  return areaKindLabel(t, key);
}

// null = not set yet (Round 2 §D): printed as 未设置 / Not set, never as a default type.
export function propertyTypeLabel(t: T, type: PropertyType | null): string {
  return type === null ? t('common.notSet') : t(PROPERTY_TYPE_KEYS[type]);
}

export function inspectionStatusLabel(t: T, status: InspectionStatus): string {
  return t(INSPECTION_STATUS_KEYS[status]);
}

export function resultLabel(t: T, status: ItemStatus): string {
  return t(RESULT_KEYS[status]);
}

export function findingClassLabel(t: T, findingClass: FindingClass): string {
  return t(FINDING_CLASS_KEYS[findingClass]);
}

export function findingStatusLabel(t: T, status: FindingStatus): string {
  return t(FINDING_STATUS_KEYS[status]);
}

export function unableReasonLabel(t: T, reason: UnableReason): string {
  return t(UNABLE_REASON_KEYS[reason]);
}

// The recorded test / operation fact of a row, named for its check (the checks whose report sentence
// depends on it — src/report/rowWording.ts checkTypeRecordsTest). The alarm label is the original
// TW-3 one; the others since 2026-09-29.
const OPERATIONAL_TEST_KEYS: Partial<Record<CheckTypeId, { label: MessageKey; hint: MessageKey }>> = Object.freeze({
  smokeCo: { label: 'item.alarmTested', hint: 'item.alarmTestedHint' },
  gfci: { label: 'item.testedGfci', hint: 'item.testedHint' },
  outlets: { label: 'item.testedOutlets', hint: 'item.testedHint' },
  windowsDoors: { label: 'item.testedWindowsDoors', hint: 'item.testedHint' },
  egress: { label: 'item.testedEgress', hint: 'item.testedHint' },
  garageDoor: { label: 'item.testedGarageDoor', hint: 'item.testedHint' },
  bathExhaust: { label: 'item.testedBathExhaust', hint: 'item.testedHint' }
});

export function operationalTestLabel(t: T, checkType: CheckTypeId): { label: string; hint: string } | null {
  const keys = OPERATIONAL_TEST_KEYS[checkType];
  return keys ? { label: t(keys.label), hint: t(keys.hint) } : null;
}

// NY F1 Round 2 — mandatory descriptors (src/domain/descriptors.ts). The catalogue is language-
// neutral DATA; these are its ONE glossary, keyed `descriptor.<key>` / `descriptorChoice.<key>.<choice>`
// (both dictionaries carry every key — tests/domain/descriptors.test.ts pins it).
export function descriptorLabel(t: T, key: DescriptorKey): string {
  return t(`descriptor.${key}` as MessageKey);
}

export function descriptorChoiceLabel(t: T, key: DescriptorKey, choice: string): string {
  return t(`descriptorChoice.${key}.${choice}` as MessageKey);
}

export function heatingFuelLabel(t: T, fuel: HeatingFuel): string {
  return descriptorChoiceLabel(t, 'heatingFuel', fuel);
}

export function utilitySourceLabel(t: T, key: 'waterSupplySource' | 'wasteDisposalSource', source: UtilitySource): string {
  return descriptorChoiceLabel(t, key, source);
}

// The printed value of one descriptor answer: the choice label (or the text), with the optional
// detail line after " · ". null when there is no answer to print.
export function descriptorValueLabel(t: T, definition: DescriptorDefinition, answer: DescriptorAnswer | null): string | null {
  if (!answer) return null;
  const value = answer.value.trim();
  const detail = answer.detail.trim();
  const base = value.length === 0 ? '' : definition.kind === 'choice' && definition.choices.includes(value) ? descriptorChoiceLabel(t, definition.key, value) : value;
  if (base.length === 0) return detail.length > 0 ? detail : null;
  return detail.length > 0 ? `${base} · ${detail}` : base;
}

export function tradeLabel(t: T, trade: ResponsibleTrade): string {
  return t(TRADE_KEYS[trade]);
}

export function basementTypeLabel(t: T, type: BasementType): string {
  return t(BASEMENT_TYPE_KEYS[type]);
}

export function basementFinishLabel(t: T, finish: BasementFinish): string {
  return t(BASEMENT_FINISH_KEYS[finish]);
}

// Why an AI request could not run / failed, in the App Language (Round 2 §AH — never a raw error).
const AI_FAILURE_KEYS: Record<string, MessageKey> = Object.freeze({
  noPhotos: 'area.aiNeedsPhotos',
  offline: 'area.aiOffline',
  notSignedIn: 'area.aiNotSignedIn',
  notConfigured: 'area.aiNotConfigured',
  locked: 'report.lockedTitle',
  busy: 'area.aiBusy',
  network: 'ai.fail.network',
  timeout: 'ai.fail.timeout',
  aborted: 'ai.fail.aborted',
  malformedResponse: 'ai.fail.malformedResponse',
  unauthenticated: 'ai.fail.unauthenticated',
  badRequest: 'ai.fail.badRequest',
  tooLarge: 'ai.fail.tooLarge',
  tooManyImages: 'ai.fail.tooManyImages',
  unsupportedImage: 'ai.fail.unsupportedImage',
  rateLimited: 'ai.fail.rateLimited',
  providerError: 'ai.fail.providerError',
  providerRefused: 'ai.fail.providerRefused',
  unavailable: 'ai.fail.unavailable',
  imageFailed: 'ai.fail.imageFailed',
  interrupted: 'ai.fail.interrupted',
  notFound: 'ai.fail.unknown',
  // NY F1 Round 1 (PP-21 / PP-20 / jurisdiction): policy blocks, not transient failures.
  blockedRealClient: 'ai.fail.blockedRealClient',
  blockedLegalReview: 'ai.fail.blockedLegalReview',
  blockedJurisdiction: 'ai.fail.blockedJurisdiction',
  // NY F1 Round 5 (OD-8): the client's own decision, stated precisely — never as a failure.
  blockedAgreementUnsigned: 'ai.fail.blockedAgreementUnsigned',
  blockedNotConsented: 'ai.fail.blockedNotConsented',
  blockedAgreementOutdated: 'ai.fail.blockedAgreementOutdated',
  // NY F1 Round 4: the inspection has no New York scope packet yet (the agreement is not signed, or
  // it was signed before the snapshot existed), so no AI result could be admitted.
  blockedNoScope: 'ai.fail.blockedNoScope',
  // Owner 2026-10-07: a Demo needs no signature for AI; what can still stop it is the property state.
  blockedNoScopeState: 'ai.fail.blockedNoScopeState',
  // Billing (Owner decisions 2026-09-11 D-3 / D-4 / X-7): the allowance is used up, or the server
  // could not confirm it. Both are states of the account, not failures of the analysis.
  quotaExhausted: 'billing.aiQuotaExhausted',
  billingUnavailable: 'billing.aiBillingUnavailable'
});

export function aiFailureMessage(t: T, code: string | null): string {
  return t(AI_FAILURE_KEYS[code ?? ''] ?? 'ai.fail.unknown');
}

// 2026-10-18: the confidence the model gave an observation, shown beside every observation (it was in
// the protocol since Round 2 and never on the screen).
export function aiConfidenceLabel(t: T, confidence: AiConfidence): string {
  return t(`ai.confidence.${confidence}` as MessageKey);
}

// The one line under an observation whose conclusion a photograph cannot settle: 「AI 没发现 ≠ 无异常」
// for "no visible defect", 「需现场测试」 for an item that must be operated. null for the others.
export function aiVerdictNote(t: T, verdict: AiVerdict | null | undefined): string | null {
  if (verdict === 'noVisibleDefect') return t('ai.verdict.noVisibleDefect');
  if (verdict === 'fieldTestRequired') return t('ai.verdict.fieldTestRequired');
  return null;
}

// ---------- NY F1 Round 4: the Scope Gate's own wording ----------

// Why a card says what it says (the gate stores codes; the App Language text lives here).
const GATE_NOTE_KEYS: Record<GateNoteCode, MessageKey> = Object.freeze({
  notRequiredNotIncluded: 'ai.scopeNote.notRequiredNotIncluded',
  notPartOfNyHomeInspection: 'ai.scopeNote.notPartOfNyHomeInspection',
  clientExcluded: 'ai.scopeNote.clientExcluded',
  uncertainSubject: 'ai.scopeNote.uncertainSubject',
  presenceQuestion: 'ai.scopeNote.presenceQuestion',
  limitationPrompt: 'ai.scopeNote.limitationPrompt',
  pestObservationOnly: 'ai.scopeNote.pestObservationOnly',
  sentenceRemoved: 'ai.scopeNote.sentenceRemoved'
});

export function gateNoteText(t: T, code: GateNoteCode): string {
  return t(GATE_NOTE_KEYS[code]);
}

// The Final Review wording reminder: where the phrase sits, and what to write instead (AI policy §4
// "allowed replacement"). Existing report / finding labels are reused wherever one exists.
const WORDING_SOURCE_KEYS: Record<WordingSource, MessageKey> = Object.freeze({
  reportSentence: 'wording.en',
  findingTitle: 'finding.title',
  findingObservation: 'finding.observation',
  findingRecommendation: 'finding.recommendation',
  findingTrade: 'finding.responsibleTrade',
  limitationNote: 'report.note',
  portionExtent: 'report.portion',
  portionNote: 'report.note',
  notPresentNote: 'report.notPresentSection',
  areaName: 'wording.source.areaName',
  areaNote: 'report.areaNote',
  itemTitle: 'wording.source.itemTitle',
  descriptorValue: 'report.description',
  photoCaption: 'wording.source.photoCaption',
  inspectorNotes: 'report.inspectorNotes',
  weather: 'report.weather'
});

export function wordingSourceLabel(t: T, source: WordingSource): string {
  return t(WORDING_SOURCE_KEYS[source]);
}

export function wordingSuggestion(t: T, phraseId: ProhibitedPhraseId): string {
  return t(`wording.suggestion.${phraseId}` as MessageKey);
}

// Priority: only "3 · Moderate" carries a frozen label (D-09); the other values show the number.
// 2026-10-19: the three defect tiers (src/domain/severity.ts). `custom` = the inspector's own name for
// the tier in this language (Settings › Report); '' or absent = the standard name.
export function severityLabel(t: T, severity: DefectSeverity, custom?: string): string {
  if (custom !== undefined && custom.trim().length > 0) return custom.trim();
  return t(severity === 'safety' ? 'severity.safety' : severity === 'repair' ? 'severity.repair' : 'severity.maintenance');
}

export function priorityLabel(t: T, priority: FindingPriority): string {
  return priority === 3 ? `3 · ${t('priority.moderate')}` : String(priority);
}

// ---------- NY F1 Round 5 (Owner rulings 2026-09-09) ----------

export function serviceLabel(t: T, service: InspectionService): string {
  return t(service === 'newBuild' ? 'service.newBuild' : 'service.homeInspection');
}

// The five-business-day reminder (approved draft §5.4), in the words that are true for the service:
// New York's own requirement for a home inspection, the agreement's promise for a New Build
// observation — which is not an Article 12-B home inspection and is not reached by RPL §444-g(3).
export function reportDueMessage(t: T, due: ReportDue, date: string): string {
  if (due.service === 'newBuild') return t(due.overdue ? 'reportDue.newBuildOverdue' : 'reportDue.newBuild', { date });
  return t(due.overdue ? 'home.reportOverdue' : 'home.reportDue', { date });
}

// The banner a finalized inspection carries. After OD-12 a generation also DELETED the working data,
// and the banner says so (approved draft §5.3); an inspection finalized before that ruling still has
// its records, and is described as it actually is.
export function lockBannerFor(t: T, inspection: Pick<V2Inspection, 'purge'>): { title: string; body: string | null } {
  if (isInspectionPurged(inspection)) return { title: t('report.purgedBanner'), body: null };
  return { title: t('report.lockedTitle'), body: t('report.lockedBody') };
}

// ---------- area instance naming (the one language-dependent part of the house plan) ----------

// zh-CN keeps the exact preset vocabulary the owner tested on device (domain default); en-US names
// are built from the dictionary. Names are generated once, at creation, in the App Language in
// force — they are inspector data afterwards.
export function planNamingFor(locale: AppLocale): PlanNaming {
  if (locale === 'zh-CN') return DEFAULT_PLAN_NAMING;
  const tr = (key: MessageKey, params?: Record<string, string | number>) => translate(locale, key, params);
  const floorLabel = (floor: number) => {
    const key = FLOOR_KEYS[floor - 1];
    return key ? tr(key) : tr('floor.n', { n: floor });
  };
  return {
    roomBase: Object.fromEntries(ROOM_PRESETS.map((p) => [p.key, tr(ROOM_BASE_KEYS[p.key])])) as Record<RoomKey, string>,
    spaceBase: Object.fromEntries(SPACE_PRESETS.map((p) => [p.key, tr(SPACE_KEYS[p.key])])) as Record<SpaceKey, string>,
    systemName: Object.fromEntries(SYSTEM_PRESETS.map((p) => [p.key, tr(AREA_KIND_KEYS[p.key])])) as Record<SystemKey, string>,
    floorPrefix: (floor: number) => `${floorLabel(floor)} `
  };
}

// ---------- house-plan summaries (dictionary versions of the domain's Chinese one-liners) ----------

// "2 floors · 3 bed · 2.5 bath · 1 kitchen · Garage · Basement · Attic" / "2 层 · 3 卧 · 2.5 卫 · 1 厨 · 车库 · 地下室 · 阁楼";
// '' when nothing is set (the UI prints 未设置 / Not set).
export function planSummaryFor(tr: Translator, plan: HousePlan): string {
  const c = planCounts(plan);
  const parts: string[] = [];
  if (c.floors > 0) parts.push(tr.tp('plan.floorsCount', c.floors));
  if (c.bedrooms > 0) parts.push(tr.t('plan.bedCount', { n: c.bedrooms }));
  if (c.baths > 0) parts.push(tr.t('plan.bathCount', { n: c.baths }));
  if (c.kitchens > 0) parts.push(tr.t('plan.kitchenCount', { n: c.kitchens }));
  for (const preset of SPACE_PRESETS) {
    const n = plan.spaces[preset.key];
    if (n <= 0) continue;
    const label = spaceLabel(tr.t, preset.key);
    parts.push(n > 1 ? `${label} ×${n}` : label);
  }
  return parts.join(' · ');
}

// "Kitchen · Living Room · Half Bath · Study" / "厨房 · 客厅 · 半卫 · 书房" — one floor's row; a count
// only when there is more than one ("Bedroom ×3").
export function floorSummaryFor(tr: Translator, floor: FloorPlan): string {
  const parts: string[] = [];
  for (const preset of ROOM_PRESETS) {
    const n = floor.rooms[preset.key];
    if (n === 1) parts.push(roomLabel(tr.t, preset.key));
    else if (n > 1) parts.push(`${roomLabel(tr.t, preset.key)}×${n}`);
  }
  parts.push(...floor.extras);
  return parts.length > 0 ? parts.join(' · ') : tr.t('plan.noRooms');
}

// The compact Property Layout rows (Round 2 §E): one row per floor, then basement, garage(s), attic,
// custom areas — each with its own edit control. Nothing is invented: unset facts print as such.
export type LayoutRowKind = 'floor' | 'basement' | 'garage' | 'attic' | 'other';

export interface LayoutRow {
  key: string;
  kind: LayoutRowKind;
  // floor index (0-based) for floors, index in plan.others for custom areas, otherwise -1.
  index: number;
  title: string;
  summary: string;
}

export function layoutRowsFor(tr: Translator, plan: HousePlan): LayoutRow[] {
  const { t } = tr;
  const rows: LayoutRow[] = plan.floors.map((floor, index) => ({ key: `floor-${index + 1}`, kind: 'floor', index, title: floorLabelFor(t, index + 1), summary: floorSummaryFor(tr, floor) }));
  if (plan.basement.type !== null || plan.basement.finish !== null) {
    const type = plan.basement.type === null ? t('layout.basementNotSet') : basementTypeLabel(t, plan.basement.type);
    const finish = plan.basement.type === 'none' || plan.basement.type === 'slab' ? null : plan.basement.finish === null ? t('layout.finishNotSet') : basementFinishLabel(t, plan.basement.finish);
    rows.push({ key: 'basement', kind: 'basement', index: -1, title: spaceLabel(t, 'basement'), summary: finish ? `${type} · ${finish}` : type });
  }
  if (plan.spaces.garage > 0 || plan.spaces.detachedGarage > 0 || plan.spaces.carport > 0) {
    const parts: string[] = [];
    if (plan.spaces.garage > 0) parts.push(plan.spaces.garage > 1 ? `${spaceLabel(t, 'garage')}×${plan.spaces.garage}` : spaceLabel(t, 'garage'));
    if (plan.spaces.detachedGarage > 0) parts.push(plan.spaces.detachedGarage > 1 ? `${spaceLabel(t, 'detachedGarage')}×${plan.spaces.detachedGarage}` : spaceLabel(t, 'detachedGarage'));
    if (plan.spaces.carport > 0) parts.push(plan.spaces.carport > 1 ? `${spaceLabel(t, 'carport')}×${plan.spaces.carport}` : spaceLabel(t, 'carport'));
    rows.push({ key: 'garage', kind: 'garage', index: -1, title: spaceLabel(t, 'garage'), summary: parts.join(' · ') });
  }
  if (plan.spaces.attic > 0) rows.push({ key: 'attic', kind: 'attic', index: -1, title: spaceLabel(t, 'attic'), summary: t('layout.present') });
  plan.others.forEach((name, index) => rows.push({ key: `other-${index}`, kind: 'other', index, title: name, summary: areaKindLabel(t, 'other') }));
  return rows;
}

export function spacesSummaryFor(tr: Translator, plan: HousePlan): string {
  const parts: string[] = [];
  for (const preset of SPACE_PRESETS) {
    const n = plan.spaces[preset.key];
    if (n <= 0) continue;
    const label = spaceLabel(tr.t, preset.key);
    parts.push(preset.counted && n > 1 ? `${label} ${n}` : label);
  }
  return parts.length > 0 ? parts.join(' · ') : tr.t('plan.noSpaces');
}

export function systemsSummaryFor(tr: Translator, plan: HousePlan): string {
  const off = SYSTEM_PRESETS.filter((preset) => !plan.systems[preset.key]).map((preset) => systemLabel(tr.t, preset.key));
  if (off.length === 0) return tr.t('plan.allSystems', { n: SYSTEM_PRESETS.length });
  if (off.length === SYSTEM_PRESETS.length) return tr.t('plan.noSystems');
  return tr.t('plan.without', { list: off.join(' / ') });
}

// ---------- display names of plan-seeded areas and template checklist rows ----------

// Areas and items are stored with the text generated in the App Language at creation (inspector
// data). Plan-seeded areas also carry a language-neutral label (AreaNameLabel) and template rows
// their template id, so the App Language and — independently — the Report Language can re-express
// them (task book §3 / §18). Inspector-named areas and inspector-added or edited rows print their
// stored text unchanged. In the creation language the display name equals the stored name.
export function areaDisplayName(locale: AppLocale, area: Pick<V2Area, 'name' | 'label' | 'floor'>): string {
  const label = area.label;
  if (!label) return area.name;
  const naming = planNamingFor(locale);
  const base =
    (naming.roomBase as Record<string, string | undefined>)[label.preset] ??
    (naming.spaceBase as Record<string, string | undefined>)[label.preset] ??
    (naming.systemName as Record<string, string | undefined>)[label.preset];
  if (!base) return area.name;
  const prefix = label.withFloor && area.floor !== null ? naming.floorPrefix(area.floor) : '';
  return `${prefix}${base}${label.index !== null ? ` ${label.index}` : ''}`;
}

function templateTextFor(locale: AppLocale, item: Pick<V2ChecklistItem, 'templateId'>): { zh: { title: string; hint: string }; en: { title: string; hint: string } } | null {
  if (locale === 'zh-CN' || !item.templateId) return null;
  const zh = templateRowById(item.templateId);
  const en = CHECKLIST_TEMPLATE_EN_US[item.templateId];
  return zh && en ? { zh, en } : null;
}

// Title of a checklist item in the given language: the en-US template row when the stored title is
// still the zh-CN template text; the stored title otherwise (custom rows, edited titles).
export function itemTitleFor(locale: AppLocale, item: Pick<V2ChecklistItem, 'templateId' | 'title'>): string {
  const text = templateTextFor(locale, item);
  return text && text.zh.title === item.title ? text.en.title : item.title;
}

// Hint of a checklist item: the template row's CURRENT hint in the given language while the stored hint
// is the template's own text — the current one or an earlier one (2026-10-27: hints sharpened since the
// item was created show their new text, src/domain/checklistTemplates.ts PREVIOUS_TEMPLATE_HINTS); the
// stored hint otherwise (custom rows).
export function itemHintFor(locale: AppLocale, item: Pick<V2ChecklistItem, 'templateId' | 'hint'>): string {
  if (!item.templateId || !isTemplateHint(item.templateId, item.hint)) return item.hint;
  if (locale === 'zh-CN') return templateRowById(item.templateId)?.hint ?? item.hint;
  return CHECKLIST_TEMPLATE_EN_US[item.templateId]?.hint ?? item.hint;
}
